import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import { ColorUtils } from '../../utils/color.utils';

// #region TEXT
export const textSymbolDefault = new TextSymbol({
  color: '#ffffff',
  font: {
    family: `Arial`,
    size: 10,
  },
  horizontalAlignment: 'center',
  kerning: true,
  haloColor: '#000000',
  haloSize: 1,
  rotated: false,
  verticalAlignment: 'baseline',
  xoffset: 0,
  yoffset: 0,
  angle: 0,
  lineWidth: 192,
  lineHeight: 1,
});

export const generateTextSymbolDefault = (
  text: string,
  cfg?: {
    fontSize?: number;
    height?: number;
    offsetY?: number;
    boxWidth?: number;
    boxHeight?: number;
    boxAspectRatio?: number;
  }
) => {
  const fontSize = cfg?.fontSize ?? 10;
  const height = cfg?.height ?? 10;

  let boxWidth: number;
  let boxHeight: number;

  if (typeof cfg?.boxWidth === 'number' && typeof cfg?.boxHeight === 'number') {
    boxWidth = cfg.boxWidth;
    boxHeight = cfg.boxHeight;
  } else {
    const boxAspectRatio = cfg?.boxAspectRatio ?? 100 / 30;
    if (typeof cfg?.boxHeight === 'number') {
      boxHeight = cfg.boxHeight;
      boxWidth = boxHeight * boxAspectRatio;
    } else {
      boxWidth = cfg?.boxWidth ?? 100;
      boxHeight = boxWidth / boxAspectRatio;
    }
  }

  const offsetY = boxHeight / 2 + (cfg?.offsetY ?? 0);

  const generateColor = (hex: string, alpha = 1) => {
    const rgba = ColorUtils.alpha(hex, alpha).rgba();
    rgba[3] = rgba[3] * 255;
    return rgba;
  };

  return new CIMSymbol({
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMPointSymbol',
        symbolLayers: [
          {
            type: 'CIMVectorMarker',
            enable: true,
            size: fontSize,
            colorLocked: true,
            anchorPointUnits: 'Relative',
            frame: {
              xmin: -5,
              ymin: -5,
              xmax: 5,
              ymax: 5,
            },
            markerGraphics: [
              {
                type: 'CIMMarkerGraphic',
                geometry: {
                  x: 0,
                  y: 0,
                },
                symbol: {
                  type: 'CIMTextSymbol',
                  fontFamilyName: 'sans-serif',
                  height,
                  horizontalAlignment: 'Center',
                  verticalAlignment: 'Center',
                  offsetX: 0,
                  offsetY,
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      {
                        type: 'CIMSolidFill',
                        enable: true,
                        color: [255, 255, 255, 255],
                      },
                    ],
                  },
                  haloSymbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [],
                  },
                },
                textString: text,
              },
            ],
            scaleSymbolsProportionally: true,
            respectFrame: true,
          },
          {
            type: 'CIMVectorMarker',
            enable: true,
            anchorPointUnits: 'Relative',
            size: boxHeight,
            offsetY,
            frame: {
              xmin: 0,
              ymin: 0,
              xmax: boxWidth,
              ymax: boxHeight,
            },
            markerGraphics: [
              {
                type: 'CIMMarkerGraphic',
                geometry: {
                  rings: [
                    [
                      [0, boxHeight],
                      [boxWidth, boxHeight],
                      [boxWidth, 0],
                      [0, 0],
                      [0, boxHeight],
                    ],
                  ],
                },
                symbol: {
                  type: 'CIMPolygonSymbol',
                  symbolLayers: [
                    {
                      type: 'CIMSolidStroke',
                      enable: true,
                      capStyle: 'Round',
                      joinStyle: 'Round',
                      miterLimit: 10,
                      width: 0,
                      color: [0, 0, 0, 255],
                    },
                    {
                      type: 'CIMSolidFill',
                      enable: true,
                      color: generateColor('#000000', 0.5),
                    },
                  ],
                },
              },
            ],
            scaleSymbolsProportionally: false,
            respectFrame: true,
          },
        ],
      },
    },
  });
};
// #endregion

// #region MEASUREMENT
export const measurement = (() => {
  const measurementColor = '#ff8000';

  const polylineSymbolDefault = new SimpleLineSymbol({
    color: measurementColor,
    width: 2,
    style: 'solid',
  });

  const polygonSymbolDefault = new SimpleFillSymbol({
    color: ColorUtils.alpha(measurementColor, 0.3).rgba(),
    style: 'solid',
    outline: polylineSymbolDefault,
  });

  const pointSymbolDefault = new SimpleMarkerSymbol({
    color: measurementColor,
    size: 12,
    outline: {
      width: 4,
      color: ColorUtils.alpha(measurementColor, 0.5).brighten(3).rgba(),
    },
  });

  return {
    polylineSymbolDefault,
    polygonSymbolDefault,
    pointSymbolDefault,
  };
})();
// #endregion

// #region PATH
export const path = (() => {
  const pathColor = '#ff8000';
  const pathDrawingColor = '#2314F5';

  const polylineSymbolDefault = new SimpleLineSymbol({
    color: pathColor,
    width: 4,
    style: 'solid',
  });

  const polylineSymbolDrawing = new SimpleLineSymbol({
    color: pathDrawingColor,
    width: 2,
    style: 'solid',
  });

  return {
    polylineSymbolDefault,
    polylineSymbolDrawing
  };
})();
// #endregion

// #region LOCALIZATION
export const localization = (() => {
  const localizationColor = '#4285f4';

  const pointSymbolDefault = new SimpleMarkerSymbol({
    color: localizationColor,
    size: 12,
    outline: {
      width: 4,
      color: ColorUtils.alpha(localizationColor, 0.5).brighten(3).rgba(),
    },
  });

  const polylineSymbolDefault = new SimpleLineSymbol({
    color: localizationColor,
    width: 2,
  });

  const polygonSymbolDefault = new SimpleFillSymbol({
    color: ColorUtils.alpha(localizationColor, 0.3).rgba(),
    style: 'solid',
    outline: polylineSymbolDefault,
  });

  return {
    pointSymbolDefault,
    polylineSymbolDefault,
    polygonSymbolDefault,
  };
})();
// #endregion

// #region INFLUENCE AREA
export const influenceArea = (() => {
  const influenceAreaColor = '#ff8000';
  const fillColor = ColorUtils.alpha(influenceAreaColor, 0.3).rgba();

  const polylineSymbol = new SimpleLineSymbol({
    color: influenceAreaColor,
    width: 2,
    style: 'solid',
  });

  const polygonDashedSymbol = new SimpleFillSymbol({
    color: fillColor,
    style: 'solid',
    outline: {
      color: influenceAreaColor,
      width: 2,
      style: 'short-dash',
    },
  });

  const polygonFillSymbol = new SimpleFillSymbol({
    color: fillColor,
    style: 'solid',
    outline: polylineSymbol,
  });

  const polygonFillWithoutOutlineSymbol = new SimpleFillSymbol({
    color: fillColor,
    style: 'solid',
    outline: {
      width: 0,
      color: 'transparent',
    },
  });

  return {
    polylineSymbol,
    polygonDashedSymbol,
    polygonFillSymbol,
    polygonFillWithoutOutlineSymbol,
  };
})();
// #endregion
